import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Mail,
  Phone,
  RefreshCw,
  Send,
  Upload,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { openSignedEnvelopePdf } from '../../../services/documents';
import {
  useFetchReviewQueue,
  useApprovePayee,
  useFollowUpPayee,
  useRejectPayee,
  useSendPayeePacket,
  useUploadPayeePaperwork,
} from '../../../services/partners';
import { friendlyDate, friendlyDateTime } from '../leads/leadHelpers';

import {
  ConfirmModal,
  FollowUpModal,
  RejectModal,
  UploadPaperworkModal,
} from './ReviewActionModals';

const CHIP =
  'inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium';

// "follow_up" → "Follow up" etc. for the note-timeline action labels.
const actionLabel = (action) => {
  if (!action) return 'Note';
  const s = String(action).replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const SkeletonCard = () => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="h-5 w-48 animate-pulse rounded-md bg-muted" />
    <div className="mt-2 h-4 w-72 animate-pulse rounded-md bg-muted" />
    <div className="mt-3 h-8 w-full max-w-sm animate-pulse rounded-md bg-muted" />
  </div>
);

// Individual referral-partner applications awaiting a human decision: signed
// paperwork came in via self-onboarding, and Approve is what mints the real
// partner (personal link + QR emailed to them).
const PartnerReviewQueue = ({ canManage, canViewPaperwork }) => {
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState(null); // { type, applicant }
  const [openNotes, setOpenNotes] = useState({}); // payeeId → bool
  const [viewingEnvelopeId, setViewingEnvelopeId] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchReviewQueue(page, {
    keepPreviousData: true,
  });
  const queue = data?.queue || [];
  const limit = data?.limit || 25;
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const firstShown = total === 0 ? 0 : (page - 1) * limit + 1;
  const lastShown = Math.min(page * limit, total);

  // Approving/rejecting the last applicant on a later page empties it — snap
  // back instead of stranding the reviewer on a pager-less "no applications".
  useEffect(() => {
    if (!isLoading && page > 1 && queue.length === 0 && total > 0) {
      setPage(Math.min(page - 1, totalPages));
    }
  }, [isLoading, page, queue.length, total, totalPages]);

  const closeModal = () => setModal(null);
  const onError = (err) => toast.error(err?.response?.data?.msg || err.message);
  const done = (msg) => () => {
    toast.success(msg);
    closeModal();
  };

  const approve = useApprovePayee(
    done('Approved — their personal link and QR code are on the way.'),
    { onError },
  );
  const followUp = useFollowUpPayee(done('Marked for follow-up.'), { onError });
  const reject = useRejectPayee(done('Application rejected.'), { onError });
  const sendPacket = useSendPayeePacket(done('New signing packet sent.'), {
    onError,
  });
  const uploadPaperwork = useUploadPayeePaperwork(done('Paperwork uploaded.'), {
    onError,
  });

  // Opens a signed envelope PDF in a new tab; toasts on failure (e.g. no access).
  const viewSignedPdf = async (envelopeId) => {
    if (viewingEnvelopeId) return;
    setViewingEnvelopeId(envelopeId);
    try {
      await openSignedEnvelopePdf(envelopeId);
    } catch (err) {
      toast.error(err?.response?.data?.msg || 'Could not open the document');
    } finally {
      setViewingEnvelopeId(null);
    }
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card px-4 py-12">
        <AlertTriangle className="h-6 w-6 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          Could not load the review queue.
        </span>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" /> Retry
        </Button>
      </div>
    );
  }

  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        No applications waiting for review.
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {queue.map((a) => {
          const notes = a.reviewNotes || [];
          const notesOpen = Boolean(openNotes[a.payeeId]);
          // A resend is "outstanding" until paperwork newer than it arrives —
          // the moment the applicant re-signs, paperworkCompletedAt passes the
          // note timestamp and the chip clears itself.
          const lastPacketSentAt = notes.reduce(
            (acc, n) => (n.action === 'packet_sent' && (!acc || n.at > acc) ? n.at : acc),
            null,
          );
          const awaitingResign = Boolean(
            lastPacketSentAt &&
              (!a.paperworkCompletedAt ||
                new Date(lastPacketSentAt) > new Date(a.paperworkCompletedAt)),
          );
          return (
            <div
              key={a.payeeId}
              className="rounded-xl border border-border bg-card p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">{a.name}</span>
                    {a.status === 'follow_up' ? (
                      <span className={cn(CHIP, 'bg-status-info-bg text-status-info-fg')}>
                        Follow-up
                      </span>
                    ) : (
                      <span
                        className={cn(CHIP, 'bg-status-warning-bg text-status-warning-fg')}
                      >
                        Needs review
                      </span>
                    )}
                    {awaitingResign ? (
                      <span
                        className={cn(CHIP, 'gap-1 bg-muted text-muted-foreground')}
                        title="A fresh signing packet was emailed — waiting on the re-signed paperwork"
                      >
                        <Send className="h-3 w-3" /> New packet sent{' '}
                        {friendlyDate(lastPacketSentAt)}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {a.email ? (
                      <span className="inline-flex items-center gap-1">
                        <Mail className="h-3.5 w-3.5" /> {a.email}
                      </span>
                    ) : null}
                    {a.phone ? (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5" /> {a.phone}
                      </span>
                    ) : null}
                    <span>
                      Submitted{' '}
                      {friendlyDate(a.paperworkCompletedAt || a.createdAt) || '—'}
                    </span>
                  </div>
                  {a.paperwork?.length ? (
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                      {/* While a resend is outstanding, every doc shown predates
                          it — the lead-in label + dimmed links read as "the old
                          set" until the re-signed paperwork arrives. */}
                      {awaitingResign ? (
                        <span className="font-medium text-muted-foreground">
                          Previous paperwork:
                        </span>
                      ) : null}
                      {canViewPaperwork ? (
                        a.paperwork.map((doc) => (
                          <button
                            key={doc.envelopeId}
                            type="button"
                            onClick={() => viewSignedPdf(doc.envelopeId)}
                            disabled={Boolean(viewingEnvelopeId)}
                            className={cn(
                              'inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-60',
                              awaitingResign && 'opacity-60',
                            )}
                          >
                            {doc.name}
                            {doc.source === 'uploaded' ? (
                              <span className="text-muted-foreground">(uploaded)</span>
                            ) : null}
                            {viewingEnvelopeId === doc.envelopeId ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : null}
                          </button>
                        ))
                      ) : (
                        <span className="text-muted-foreground">
                          {a.paperwork.length} signed document
                          {a.paperwork.length === 1 ? '' : 's'} on file
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>

                {canManage ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Button
                      size="sm"
                      onClick={() => setModal({ type: 'approve', applicant: a })}
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setModal({ type: 'follow_up', applicant: a })}
                    >
                      Follow-up
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setModal({ type: 'reject', applicant: a })}
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setModal({ type: 'send_packet', applicant: a })}
                    >
                      <Send className="mr-1.5 h-3.5 w-3.5" /> Send new packet
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setModal({ type: 'upload', applicant: a })}
                    >
                      <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload paperwork
                    </Button>
                  </div>
                ) : null}
              </div>

              {notes.length ? (
                <div className="mt-3 border-t border-border pt-2">
                  <button
                    type="button"
                    onClick={() =>
                      setOpenNotes((prev) => ({
                        ...prev,
                        [a.payeeId]: !prev[a.payeeId],
                      }))
                    }
                    className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 transition-transform',
                        notesOpen && 'rotate-180',
                      )}
                    />
                    Notes ({notes.length})
                  </button>
                  {notesOpen ? (
                    <ul className="mt-2 space-y-2">
                      {notes.map((n, i) => (
                        <li key={i} className="rounded-lg bg-muted px-3 py-2 text-sm">
                          <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {n.byName || 'Unknown'}
                            </span>
                            <span>·</span>
                            <span>{actionLabel(n.action)}</span>
                            <span>·</span>
                            <span>{friendlyDateTime(n.at) || '—'}</span>
                          </div>
                          {n.note ? (
                            <p className="mb-0 mt-1 text-foreground">{n.note}</p>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Pager */}
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {total === 0
            ? 'No applications'
            : `Showing ${firstShown}–${lastShown} of ${total}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-md border border-border p-1 hover:bg-accent disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Action modals — conditionally mounted per applicant so state starts fresh. */}
      {modal?.type === 'approve' && canManage ? (
        <ConfirmModal
          title="Approve application"
          body={
            <>
              Approve <span className="font-medium">{modal.applicant.name}</span>{' '}
              as a referral partner? They&apos;ll receive their personal referral
              link and QR code by email.
            </>
          }
          confirmLabel="Approve"
          onClose={closeModal}
          onConfirm={() => approve.mutate(modal.applicant.payeeId)}
          isSaving={approve.isLoading}
        />
      ) : null}
      {modal?.type === 'follow_up' && canManage ? (
        <FollowUpModal
          applicant={modal.applicant}
          onClose={closeModal}
          onSubmit={({ note }) =>
            followUp.mutate({ payeeId: modal.applicant.payeeId, note })
          }
          isSaving={followUp.isLoading}
        />
      ) : null}
      {modal?.type === 'reject' && canManage ? (
        <RejectModal
          applicant={modal.applicant}
          onClose={closeModal}
          onSubmit={({ note }) =>
            reject.mutate({ payeeId: modal.applicant.payeeId, note })
          }
          isSaving={reject.isLoading}
        />
      ) : null}
      {modal?.type === 'send_packet' && canManage ? (
        <ConfirmModal
          title="Send new packet"
          body={
            <>
              Email <span className="font-medium">{modal.applicant.name}</span> a
              fresh signing link? Use this when their paperwork needs to be
              re-signed or the original link expired.
            </>
          }
          confirmLabel="Send packet"
          onClose={closeModal}
          onConfirm={() => sendPacket.mutate(modal.applicant.payeeId)}
          isSaving={sendPacket.isLoading}
        />
      ) : null}
      {modal?.type === 'upload' && canManage ? (
        <UploadPaperworkModal
          title={`Upload paperwork — ${modal.applicant.name}`}
          description="Attach signed PDFs collected outside the e-sign flow (they'll appear with an (uploaded) marker)."
          onClose={closeModal}
          onUpload={(files) =>
            uploadPaperwork.mutate({ payeeId: modal.applicant.payeeId, files })
          }
          isSaving={uploadPaperwork.isLoading}
        />
      ) : null}
    </>
  );
};

export default PartnerReviewQueue;
